import type { FeatureImplementation } from '@headless-tree/core';

interface SelectionAnchorDataRef {
  selectUpToAnchorId?: string | null;
}

interface SelectionAnchorTree {
  getDataRef: <T>() => { current: T };
}

export function setSelectionAnchor(tree: SelectionAnchorTree, itemId: string) {
  tree.getDataRef<SelectionAnchorDataRef>().current.selectUpToAnchorId = itemId;
}

/** Shared click handler: shift/ctrl-meta selection, set focused, primary action */
function handleItemClick(
  e: React.MouseEvent,
  tree: SelectionAnchorTree & { setSelectedItems: (ids: string[]) => void },
  item: { selectUpTo: (addToSelection: boolean) => void; toggleSelect: () => void; getItemMeta: () => { itemId: string }; setFocused: () => void; primaryAction: () => void },
) {
  if (e.currentTarget instanceof HTMLElement) {
    e.currentTarget.focus();
  }
  if (e.shiftKey) item.selectUpTo(e.ctrlKey || e.metaKey);
  else if (e.ctrlKey || e.metaKey) item.toggleSelect();
  else tree.setSelectedItems([item.getItemMeta().itemId]);
  if (!e.shiftKey) setSelectionAnchor(tree, item.getItemMeta().itemId);
  item.setFocused();
  item.primaryAction();
}

/** Base custom click behaviour feature: multi-selection (shift/ctrl-meta) + focus + primary action. */
export const customClickBehaviorFeature: FeatureImplementation = {
  key: 'custom-click-behavior',
  itemInstance: {
    getProps: ({ tree, item, prev }) => {
      const prevProps = prev?.() ?? {};
      return {
        ...prevProps,
        onClick: (e: React.MouseEvent) => handleItemClick(e, tree, item),
      };
    },
  },
};

export interface CustomClickBehaviorDragOptions {
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: (e: DragEvent) => void;
}

/**
 * Creates a custom click behaviour feature with optional drag lifecycle hooks.
 * Use when the tree has drag-and-drop and you need to run code on drag start/end (e.g. drag preview).
 * Custom handlers run first, then the previous feature's onDragStart/onDragEnd.
 */
export function createCustomClickBehaviorFeature(
  options: CustomClickBehaviorDragOptions,
): FeatureImplementation {
  const { onDragStart, onDragEnd } = options;
  return {
    key: 'custom-click-behavior',
    itemInstance: {
      getProps: ({ tree, item, prev }) => {
        const prevProps = prev?.() ?? {};
        return {
          ...prevProps,
          onClick: (e: React.MouseEvent) => handleItemClick(e, tree, item),
          ...(onDragStart && {
            onDragStart: (e: DragEvent) => {
              onDragStart(e);
              prevProps.onDragStart?.(e);
            },
          }),
          ...(onDragEnd && {
            onDragEnd: (e: DragEvent) => {
              onDragEnd(e);
              prevProps.onDragEnd?.(e);
            },
          }),
        };
      },
    },
  };
}
